/*
====================================================================
                    Componente ProgressCircle
====================================================================
Este componente visualiza un círculo de progreso utilizando SVG. El 
progreso se muestra como un círculo que se rellena en función de un 
porcentaje proporcionado.

- Visualiza el progreso como un círculo que se va completando.
- Permite personalizar el radio y el grosor del círculo.
- Utiliza animaciones para una transición suave del progreso.

====================================================================
*/

// ------------------------------------------------------------------
//                            IMPORTS
// ------------------------------------------------------------------
// Importación de librerías estándar de React.

import React from "react";

// ------------------------------------------------------------------
//                       DEFINICIÓN DEL COMPONENTE
// ------------------------------------------------------------------
/**
 * Componente para visualizar un círculo de progreso.
 *
 * Este componente muestra un círculo de progreso basado en un valor de porcentaje
 * que determina cuánto del círculo está lleno.
 *
 * @param {object} props
 * @param {number} props.progress - El porcentaje de progreso (entre 0 y 100).
 *
 * @returns {JSX.Element} Un SVG que representa el progreso como un círculo.
 */
const ProgressCircle = ({
  progress = 0,
  color_fondo = "#641f2000",
  color_progreso = "#00000033",
  svgClassName = "", // Nueva propiedad para la clase del SVG
  tamaño=24, // Tamaño del  círculo
  strokeWidth=4, // Grosor del borde
  rotate=-90, // Rotación del círculo
}) => {
  tamaño = tamaño === 0 ? 24 : tamaño; // Asigna el valor por defecto si es 0
  const radius = tamaño/2; // Radio del círculo
  const stroke = strokeWidth; // Grosor del borde
  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <svg
      className={`${svgClassName}`} // Combina las clases
      height={radius * 2}
      width={radius * 2}
      style={{ transform: `rotate(${rotate}deg)` }} // Rotación de 90 grados en sentido antihorario
    >
      {/* Círculo de fondo */}
      <circle
        stroke={color_fondo}
        fill="transparent"
        strokeWidth={stroke}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
      />
      {/* Círculo de progreso */}
      <circle
        stroke={color_progreso}
        fill="transparent"
        strokeWidth={stroke}
        strokeDasharray={circumference + " " + circumference}
        style={{
          strokeDashoffset,
          transition: "stroke-dashoffset 0.1s linear",
        }}
        r={normalizedRadius}
        cx={radius}
        cy={radius}
      />
    </svg>
  );
};

// ------------------------------------------------------------------
//                            EXPORTS
// ------------------------------------------------------------------
// Exportación del componente para su uso en otros módulos de la aplicación.

export default ProgressCircle;
